import { Component, OnInit, ViewChild, ElementRef, NgZone } from '@angular/core'
import { ResourceService } from '../../../common/services/resource.service'
import { Router, ActivatedRoute } from '@angular/router'
import { Mission } from '../../../common/interfaces/mission.interface'
import {
  FormBuilder,
  FormGroup,
  Validators,
  FormControl,
  ValidatorFn
} from '@angular/forms'
import { HttpParams } from '@angular/common/http'
import { HasDatepicker } from '../../../common/base-components/has-datepicker'
import { Field } from '../../../common/interfaces/field.interface'
import { FlashMessagesService } from 'angular2-flash-messages'
import { appConstants } from '../../../app.constants'
import { Sample } from '../../../common/interfaces/sample.interface'
import { Answer } from '../../../common/interfaces/answer.interface'
import { BreadcrumbService } from '../../../common/services/breadcrumb.service'
import { environment } from '../../../../environments/environment'
import { AuthService } from '../../../common/services/auth.service'
import { User } from '../../../common/interfaces/user.interface'
import { Role } from '../../../common/enums/role.enum'
import { NativescriptInterfaceService } from '../../../common/services/nativescript-interface.service'
import { isNull } from 'util'
import { ConnectivityService } from '../../../common/services/offline/connectivity.service'
import { OfflineResourceService } from '../../../common/services/offline/offline-resource.service'

@Component({
  selector: 'app-sample-create-edit',
  templateUrl: './sample-create-edit.component.html',
  styleUrls: ['./sample-create-edit.component.scss'],
  providers: [ResourceService]
})
export class SampleCreateEditComponent extends HasDatepicker implements OnInit {
  mission: Mission
  sample: Sample
  users: User[]
  // Chargés de mission sélectionnables : role=User + même société que le saisisseur.
  assignableUsers: User[] = []
  currentUserCompanyId: number = null
  mode: string

  netWeight: number
  dynamicFieldIds: number[] = []
  storagePath: string = environment.storagePath
  requiredValidator: ValidatorFn[] = [Validators.required]

  // Per-image validator arrays, recomputed from mission.minPhotos once the
  // mission is loaded. Stable references so abc-image-input's ngOnChanges only
  // fires (and the "required" asterisk updates) when the requirement actually
  // changes. Default mirrors the historical 3-required behaviour.
  imageValidators: { [key: string]: ValidatorFn[] } = {
    image1: this.requiredValidator,
    image2: this.requiredValidator,
    image3: this.requiredValidator,
    image4: [],
    image5: [],
    image6: [],
    image7: [],
    image8: []
  }

  currentUser: User
  Role = Role
  loading: boolean
  submitLoading: boolean
  isOnline = true

  // Only for embedded version
  clickedImageInput: string
  // On native version, files are not directly uploaded through input so we keep track of adding to reproduce the similar interaction.
  nativeFilesUploaded = {
    image1: false,
    image2: false,
    image3: false,
    image4: false,
    image5: false,
    image6: false,
    image7: false,
    image8: false
  }

  // Photos loaded from IndexedDB for offline-created samples — fed into image-input as initialBlob to render thumbnails.
  initialBlobs: { [key: string]: Blob } = {}

  form: FormGroup = this.formBuilder.group({
    missionId: [null, Validators.required],
    name: ['', Validators.required],
    receiptDate: [null],
    origin: ['', Validators.required],
    weight: [null, Validators.required],
    tare: '',
    image1: [null, Validators.required],
    image2: [null, Validators.required],
    image3: [null, Validators.required],
    image4: null,
    image5: null,
    image6: null,
    image7: null,
    image8: null,
    // Chargés de mission affectés : tableau d'ids (relation N:M Sample.users).
    assignedUserIds: [[]]
  })

  constructor(
    private resourceService: ResourceService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private formBuilder: FormBuilder,
    private flashMessagesService: FlashMessagesService,
    private breadcrumbService: BreadcrumbService,
    private nativescriptInterfaceService: NativescriptInterfaceService,
    private authService: AuthService,
    private ngZone: NgZone,
    private connectivityService: ConnectivityService,
    private offlineResourceService: OfflineResourceService
  ) {
    super()
  }

  ngOnInit() {
    this.connectivityService.isOnline$.subscribe(online => {
      this.isOnline = online
    })

    this.mode = this.activatedRoute.snapshot.data.mode

    // Société du saisisseur — sert à restreindre les chargés de mission sélectionnables.
    this.authService.getFullMe().then((me: User) => {
      this.currentUserCompanyId = me && me.company ? me.company.id : null
      this.computeAssignableUsers()
    })

    this.authService.currentUser.subscribe((userRes: User) => {
      this.currentUser = userRes

      this.activatedRoute.params.subscribe(
        (params: { missionId?: string; id?: string }) => {
          this.loading = true

          // Load users list (non-blocking: don't gate mission/sample loading on this)
          if (this.isOnline) {
            let userListParams = new HttpParams()
            userListParams = userListParams.set('withoutPagination', 'true')
            this.resourceService
              .list('users', userListParams)
              .subscribe(
                (userListRes: User[]) => {
                  this.users = userListRes
                  this.computeAssignableUsers()
                },
                () => {}
              )
          }

          if (this.mode === 'createFromMission') {
            this.offlineResourceService.getMissions().then(
              (missionRes: Mission[]) => {
                this.loading = false
                this.form.get('missionId').setValue(params.missionId)
                this.mission = missionRes.find(
                  (m: Mission) => m.id === parseInt(params.missionId, 10)
                )
                this.applyPhotoValidators(this.mission)
                this.setDynamicFields(this.mission)

                this.breadcrumbService.breadcrumbLinks.next([
                  {
                    path: '/missions',
                    label: 'Missions'
                  },
                  {
                    path: '/missions/' + this.mission.id,
                    label: this.mission.name
                  },
                  {
                    label: 'Nouveau prélèvement'
                  }
                ])
              },
              () => { this.loading = false }
            )
          } else if (this.mode === 'edit') {
            this.offlineResourceService.getSample(params.id).then(
              (sampleRes: Sample) => {
                this.loading = false

                this.sample = sampleRes
                this.mission = this.sample.mission

                this.applyPhotoValidators(this.mission)

                // Append basic data into form first (before dynamic fields)
                this.form.get('missionId').setValue(sampleRes.mission.id)
                this.form.get('name').setValue(sampleRes.name)
                this.form.get('origin').setValue(sampleRes.origin)
                this.form.get('weight').setValue(sampleRes.weight)

                this.form.get('image1').setValue(sampleRes.image1)
                this.form.get('image2').setValue(sampleRes.image2)
                this.form.get('image3').setValue(sampleRes.image3)
                this.form.get('image4').setValue(sampleRes.image4)
                this.form.get('image5').setValue(sampleRes.image5)
                this.form.get('image6').setValue(sampleRes.image6)
                this.form.get('image7').setValue(sampleRes.image7)
                this.form.get('image8').setValue(sampleRes.image8)

                // For offline-created samples, photos are stored in IndexedDB, not as URLs.
                // Load them and set as { name, content } so the form and image-input work properly.
                if (sampleRes._pendingSync) {
                  this.offlineResourceService.getPhotos('sample', sampleRes.id).then(photos => {
                    const nextBlobs: { [key: string]: Blob } = {}
                    Object.keys(photos).forEach(fieldName => {
                      const blob = photos[fieldName]
                      this.form.get(fieldName).setValue({ name: 'Photo enregistrée', content: blob })
                      nextBlobs[fieldName] = blob
                      this.nativeFilesUploaded[fieldName] = true
                    })
                    this.initialBlobs = nextBlobs
                  })
                }

                // Pré-sélection des chargés de mission affectés
                if (sampleRes.users) {
                  this.form
                    .get('assignedUserIds')
                    .setValue(sampleRes.users.map((u: User) => u.id))
                }

                if (sampleRes.tare) {
                  this.form.get('tare').setValue(sampleRes.tare)
                }
                if (sampleRes.receiptDate) {
                  this.form
                    .get('receiptDate')
                    .setValue(
                      this.formatStandardDate(sampleRes.receiptDate)
                    )
                }

                // Set dynamic fields (guarded: mission.fields may be absent from cache)
                if (this.mission && this.mission.fields) {
                  this.setDynamicFields(this.mission)

                  // Populate answers for dynamic fields
                  if (sampleRes.answers) {
                    sampleRes.answers.forEach((a: Answer) => {
                      const control = this.form.get(
                        'field' + a.field.id.toString()
                      )
                      if (control) {
                        let value;
                          switch (a.field.type) {
                            case "0":
                              value = a.value === 'Oui' ? true : false;
                              break;
                            case "1":
                              value = a.value;
                              break
                            case "2":
                              value = a.value;
                              break;
                            case "3":
                              value = a.value.split(' | ');
                              break;
                            default:
                                value = a.value;
                                break;
                          }
                        control.setValue(value)
                      }
                    })
                  }
                }

                this.breadcrumbService.breadcrumbLinks.next([
                  {
                    path: '/samples',
                    label: 'Prélèvements'
                  },
                  {
                    path: '/samples/' + sampleRes.id,
                    label: sampleRes.name
                  },
                  {
                    label: 'Editer'
                  }
                ])
              },
              () => { this.loading = false }
            )
          }

          // Calculate live net weight
          this.form.valueChanges.subscribe(
            (formValues: { tare: number; weight: number }) => {
              if (formValues.weight) {
                this.netWeight =
                  Math.round(
                    (formValues.weight - (formValues.tare || 0)) * 100
                  ) / 100
              }
            }
          )
        }
      )
    })

    // Define native interface functions to get image files from camera/filepicker on embedded version
    if (environment.nativescriptEmbedded) {
      this.defineInterfaceFunctions()
    }
  }

  submit(form: FormGroup): void {
    this.submitLoading = true

    const formData = new FormData()
    formData.append('missionId', form.value.missionId)
    formData.append('name', form.value.name)
    formData.append('origin', form.value.origin)
    formData.append('weight', form.value.weight)

    formData.append(
      'receiptDate',
      form.value.receiptDate
        ? this.formatMyDatePickerDate(form.value.receiptDate)
        : ''
    )
    formData.append('tare', form.value.tare || 0)

    // Chargés de mission affectés. Chaîne vide envoyée si aucun sélectionné, afin de
    // pouvoir vider la sélection lors d'une mise à jour (sinon le backend la conserve).
    const assignedUserIds: number[] = form.value.assignedUserIds || []
    if (assignedUserIds.length) {
      assignedUserIds.forEach((userId: number) => {
        formData.append('userIds', userId.toString())
      })
    } else {
      formData.append('userIds', '')
    }

    for (let index = 1; index <= 8; index++) {
      const imageName = 'image' + index
      if (
        form.value[imageName] &&
        form.value[imageName].content &&
        form.value[imageName].name
      ) {
        formData.append(
          imageName,
          form.value[imageName].content,
          form.value[imageName].name
        )
      }

      // For optional images , if we removed image, we send empty value
      if (!form.value[imageName] && index >= 4) {
        formData.append(imageName, '')
      }
    }

    // Answers to dynamicFields
    if (this.dynamicFieldIds.length) {
      const answerObjects: { fieldId: number; value: string }[] = []
      this.mission.fields.forEach((field: Field) => {
        if (form.value['field' + field.id] != null) {
          let value;
          switch (field.type) {
            case "0":
              value = form.value['field' + field.id] === true ? 'Oui' : 'Non';
              break;
            case "1":
              value = form.value['field' + field.id];
              break
            case "2":
              value = form.value['field' + field.id];
              break;
            case "3":
              value = form.value['field' + field.id] ? form.value['field' + field.id].join(' | ') : '';
              break;
            default:
                value = form.value['field' + field.id];
                break;
          }

          answerObjects.push({
            fieldId: field.id,
            value
          })
        }
      })

      formData.append('answerObjects', JSON.stringify(answerObjects))
    }

    if (this.mode === 'create' || this.mode === 'createFromMission') {
      if (!this.isOnline) {
        // Offline: queue the operation
        this.offlineResourceService.saveSample(formData, 'create').then(
          (localSample: any) => {
            this.submitLoading = false
            this.flashMessagesService.show(
              `Prélèvement enregistré localement, sera synchronisé au retour du réseau.`,
              {
                cssClass: 'notification is-blue',
                timeout: appConstants.FLASH_MESSAGE_TIMEOUT
              }
            )
            setTimeout(() => {
              this.router.navigate(['/samples'])
            }, 100)
          },
          (err) => {
            this.submitLoading = false
            this.flashMessagesService.show(
              `Une erreur est survenue lors de l'enregistrement local.`,
              {
                cssClass: 'notification is-danger',
                timeout: appConstants.FLASH_MESSAGE_TIMEOUT
              }
            )
          }
        )
      } else {
        this.resourceService.store('samples', formData).subscribe(
          (createdSample: Sample) => {
            this.submitLoading = false
            this.flashMessagesService.show(`Le prélèvement a bien été créé`, {
              cssClass: 'notification is-success',
              timeout: appConstants.FLASH_MESSAGE_TIMEOUT
            })
            setTimeout(() => {
              this.router.navigate(['/samples', createdSample.id])
            }, 100)
          },
          (err) => {
            this.submitLoading = false
            this.flashMessagesService.show(
              'Error ' +
                JSON.stringify(err.error.message.map((m) => m.constraints)),
              {
                cssClass: 'notification is-danger',
                timeout: appConstants.FLASH_MESSAGE_TIMEOUT
              }
            )
          }
        )
      }
    } else {
      // Update existing resource
      if (!this.isOnline) {
        this.offlineResourceService.saveSample(formData, 'update', this.sample.id).then(
          () => {
            this.submitLoading = false
            this.flashMessagesService.show(
              `Prélèvement mis à jour localement, sera synchronisé au retour du réseau.`,
              {
                cssClass: 'notification is-blue',
                timeout: appConstants.FLASH_MESSAGE_TIMEOUT
              }
            )
            setTimeout(() => {
              this.router.navigate(['/samples', this.sample.id])
            }, 100)
          },
          (err) => {
            this.submitLoading = false
            this.flashMessagesService.show(
              `Une erreur est survenue lors de l'enregistrement local.`,
              {
                cssClass: 'notification is-danger',
                timeout: appConstants.FLASH_MESSAGE_TIMEOUT
              }
            )
          }
        )
      } else {
        this.resourceService
          .update('samples', this.sample.id, formData)
          .subscribe(
            (res) => {
              this.submitLoading = false
              this.flashMessagesService.show(
                `Le prélèvement a bien été mis à jour`,
                {
                  cssClass: 'notification is-success',
                  timeout: appConstants.FLASH_MESSAGE_TIMEOUT
                }
              )
              setTimeout(() => {
                this.router.navigate(['/samples', this.sample.id])
              }, 100)
            },
            (err) => {
              this.submitLoading = false
              this.flashMessagesService.show(
                'Error ' +
                  JSON.stringify(err.error.message.map((m) => m.constraints)),
                {
                  cssClass: 'notification is-danger',
                  timeout: appConstants.FLASH_MESSAGE_TIMEOUT
                }
              )
            }
          )
      }
    }
  }

  // Applies the mission's minPhotos rule: the first N image slots become
  // required, the rest optional. Updates both the form control validators
  // (which gate the submit button via form.invalid) and the per-image
  // validator arrays passed to abc-image-input (visual "required" state).
  applyPhotoValidators(mission: Mission) {
    const minPhotos: number =
      mission && mission.minPhotos != null ? mission.minPhotos : 3
    const clamped: number = Math.max(0, Math.min(8, minPhotos))

    const nextValidators: { [key: string]: ValidatorFn[] } = {}
    for (let index = 1; index <= 8; index++) {
      const controlName = 'image' + index
      const validators: ValidatorFn[] =
        index <= clamped ? this.requiredValidator : []
      nextValidators[controlName] = validators

      const control = this.form.get(controlName)
      control.setValidators(validators)
      control.updateValueAndValidity()
    }
    this.imageValidators = nextValidators
  }

  setDynamicFields(mission: Mission) {
    // Set dynamic fields
    mission.fields.sort((a, b) => {return a.position > b.position ? 1 : a.position < b.position ? -1 : 0}).forEach((f: Field) => {
      const controlName = 'field' + f.id.toString()
      const validators = f.required ? [Validators.required] : []

      this.form.addControl(controlName, new FormControl('', validators))
      this.dynamicFieldIds.push(f.id)
    })
  }

  // Triggers on adding image
  setImage(
    imageEvent: { name: string; content: File | any },
    propName: string
  ) {
    this.form.get(propName).setValue(imageEvent)

    if (isNull(imageEvent)) {
      this.nativeFilesUploaded[propName] = false
      if (this.initialBlobs[propName]) {
        const next = { ...this.initialBlobs }
        delete next[propName]
        this.initialBlobs = next
      }
    }
  }

  defineInterfaceFunctions() {
    window.setFileToUpload = (base64file: string) => {
      const blob = this.setImage(
        {
          name: 'Image from Native',
          content: this.nativescriptInterfaceService.base64toBlob(
            base64file,
            'image/jpg'
          )
        },

        this.clickedImageInput
      )
      this.ngZone.run(() => {
        this.nativeFilesUploaded[this.clickedImageInput] = true
        delete this.clickedImageInput
      })
    }
  }

  optionsToString(options: Array<{id: number, value: string}>) {
    return options.map(o=> o.value);
  }

  // Filtre la liste des utilisateurs aux intervenants affectables — chargés de mission
  // (role=User) uniquement — actifs et de la même société que le saisisseur. Recalculé
  // dès que la liste users ou la société du saisisseur est connue (ordre de résolution
  // non garanti).
  computeAssignableUsers() {
    if (!this.users || this.currentUserCompanyId == null) {
      this.assignableUsers = []
      return
    }
    this.assignableUsers = this.users.filter(
      (u: User) =>
        u.role === Role.User &&
        u.active &&
        u.company &&
        u.company.id === this.currentUserCompanyId
    )
  }

  // Mappe les intervenants sélectionnables vers le format attendu par <multiselect>
  // ({ id, label }). Tous étant des chargés de mission, seul le nom est affiché.
  get assignableUserItems(): { id: number; label: string }[] {
    return this.assignableUsers.map((u: User) => ({
      id: u.id,
      label: u.name
    }))
  }

}
